/**
 * GlobalAdminRoute — 전역 관리자만 통과하는 라우트 가드.
 *
 * AdminRoute보다 엄격하다. superadmin 또는 매장 소속이 없는 admin(tenantId == null)만
 * 통과시키고, 매장 소속 admin("매장대표")은 "/"로 리다이렉트한다.
 * 시스템 기능(매장 관리·크롤링 관리·데이터 카탈로그)을 매장대표로부터 막는 데 쓴다.
 *
 * 로딩 처리는 AdminRoute와 동일한 패턴을 따른다 — profile fetch가 일시 장애로
 * 너무 오래 멈춰 있으면 무한 스피너 대신 복구 안내를 띄운다.
 */

import { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router";
import { useAuth } from "../../context/AuthContext";

function Spinner() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
    </div>
  );
}

function StalledNotice() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 px-4 text-center">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        서버 응답이 지연되고 있어요. 잠시 후 다시 시도해주세요.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600"
      >
        새로고침
      </button>
    </div>
  );
}

export default function GlobalAdminRoute({ allowStoreOwner = false }: { allowStoreOwner?: boolean } = {}) {
  const { user, profile, loading, profileLoading, isGlobalAdmin } = useAuth();

  // user는 있는데 profile이 12초 넘게 null이면 일시 장애로 보고 복구 안내를 띄운다.
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    if (!loading && !profileLoading && user && profile === null) {
      const t = setTimeout(() => setStalled(true), 12000);
      return () => clearTimeout(t);
    }
    setStalled(false);
  }, [loading, profileLoading, user, profile]);

  // auth 초기 로딩 또는 profile fetch 진행 중
  if (loading || profileLoading) return <Spinner />;

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // user가 있지만 profile이 null: fetchProfile 일시 실패. 리다이렉트하지 않고 spinner 유지하되,
  // 너무 오래(12초+) 지속되면 복구 안내 화면으로 전환한다.
  if (profile === null) return stalled ? <StalledNotice /> : <Spinner />;

  // 기본은 전역 관리자만. allowStoreOwner 면 매장대표(owner)도 통과시킨다
  // (데이터 카탈로그 읽기 전용 열람 — 수정은 백엔드/UI에서 별도로 막는다).
  const allowed = isGlobalAdmin() || (allowStoreOwner && profile.role === "owner");
  if (!allowed) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
